// FrameSelector.js
import React, { useEffect } from "react";
import "../style/FrameSelector.css";
import { useIsSquare } from "../hooks/useIsSquare";
import Icon from '@mdi/react';


import {
  mdiInformationVariantCircle ,
} from '@mdi/js';

const FrameSelector = ({ frameSettings, setFrameSettings, frameOptions = {}, personalSettings}) => {
  // Defensive defaults (frameOptions may be empty during initial load)
  const materials = Array.isArray(frameOptions.materials) ? frameOptions.materials : [];
  const sizes = (frameOptions.sizes && typeof frameOptions.sizes === 'object') ? frameOptions.sizes : {};
  const orientations = Array.isArray(frameOptions.orientations) ? frameOptions.orientations : (frameOptions.orientations || [{id: 'square', label: 'Square'}, {id: 'portrait', label: 'Portrait'}]);
  const frames = Array.isArray(frameOptions.frames) ? frameOptions.frames : [];

  const orientationSizes = {
    square: "12x12",
    portrait: "12x16",
  };

  const isSquare = useIsSquare(personalSettings);

  // Helpers for resolving URLs in dev vs production
  const WP_BASE = (typeof window !== 'undefined' && window.__GELATO_BASE_URL__) ? window.__GELATO_BASE_URL__ : '';
  const PLACEHOLDER = (typeof window !== 'undefined' && window.__GELATO_PLACEHOLDER_FRAME__) ? window.__GELATO_PLACEHOLDER_FRAME__ : '/placeholder-frame.png';

  
  const scrollToSectionId = (id) => {
    console.log('Scrolling to section', id);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };
  function resolveUrl(u) {
    if (!u) return PLACEHOLDER;
    u = String(u).trim();
    if (u.indexOf('http://') === 0 || u.indexOf('https://') === 0) return u;
    if (u[0] === '/') {
      if (WP_BASE) return WP_BASE.replace(/\/$/, '') + u;
      return u; // will be relative to current origin
    }
    // relative path without leading slash
    if (WP_BASE) return WP_BASE.replace(/\/$/, '') + '/' + u;
    return u;
  }

  // Safely get preview URL for a frame for current orientation/size
  function getPreviewUrl(frame, orientation, sizeId) {
    if (!frame) return PLACEHOLDER;

    console.log('Get preview for frame', frame.thumb);
    // 1) try sizes[orientation][sizeId].url
    /* try {
      const sizesMap = frame.sizes || {};
      if (orientation && sizeId && sizesMap[orientation] && sizesMap[orientation][sizeId]) {
        const candidate = sizesMap[orientation][sizeId].url;
        if (candidate && candidate.trim() !== '') return resolveUrl(candidate);
      }
    } catch (e) {
      // ignore and fall through
    } */

    // 2) try frame.thumb
    if (frame.thumb && typeof frame.thumb === 'string' && frame.thumb.trim() !== '') {
      return frame.thumb;//resolveUrl(frame.thumb);
    }

    // 3) try first variant image if exists
    /* if (Array.isArray(frame.variants) && frame.variants.length) {
      const vimg = frame.variants[0].image_url || frame.variants[0].image || '';
      if (vimg) return resolveUrl(vimg);
    } */

    // 4) fallback
    return PLACEHOLDER;
  }

  // Handlers
  const handleMaterialChange = (materialId) => {
    // if switching to canvas ensure frame is none
    const next = { ...frameSettings, material: materialId };
    if (materialId === 'canvas') next.frame = 'none';
    setFrameSettings(next);
  };
  

  const handleSizeChange = (e) => {
    setFrameSettings({ ...frameSettings, size: e.target.value });
  };

  const handleOrientationChange = (orientationId) => {
    setFrameSettings((prev) => ({
      ...prev,
      orientation: orientationId,
      size: orientationSizes[orientationId] || prev.size,
    }));
  };

  const handleFrameChange = (frameId) => {
    setFrameSettings({ ...frameSettings, frame: frameId });
  };

  // defensive: if current orientation not present in sizes, pick one
  useEffect(() => {
    if (!frameSettings) return;
    if (!frameSettings.orientation && orientations && orientations.length) {
      setFrameSettings(prev => ({ ...prev, orientation: orientations[0].id }));
      return;
    }
    // ensure size exists for orientation
    const orient = frameSettings.orientation;
    if (orient && sizes && sizes[orient] && Array.isArray(sizes[orient]) && sizes[orient].length) {
      const sizeIds = sizes[orient].map(s => s.id);
      if (!sizeIds.includes(frameSettings.size)) {
        // pick first available size for this orientation
        setFrameSettings(prev => ({ ...prev, size: sizes[orient][0].id }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameOptions]);

  // Render helpers
  const materialButtons = materials.map((m) => {
    const id = m.id || m;
    const label = m.label || String(m);
    const sel = frameSettings && frameSettings.material === id;
    return (
      <button
        key={id}
        className={`toggle-button ${sel ? 'selected' : ''}`}
        onClick={() => handleMaterialChange(id)}
      >
        {label}
      </button>
    );
  });

  const sizeOptions = (() => {
    const orient = frameSettings && frameSettings.orientation;
    const list = (orient && sizes[orient]) ? sizes[orient] : [];
    if (!Array.isArray(list)) return null;
    return list.map(s => (<option key={s.id} value={s.id}>{s.label || s.id}</option>));
  })();

  const orientationButtons = (orientations || []).map(o => {
    const id = o.id || o;
    const label = o.label || String(o);
    const disabled = id === 'square' && !isSquare;
    return (
      <button
        key={id}
        className={`toggle-button ${frameSettings && frameSettings.orientation === id ? 'selected' : ''}`}
        onClick={() => handleOrientationChange(id)}
        disabled={disabled}
      >
        {label}
      </button>
    );
  });

  const frameItems = (frames || []).map(frame => {
    const isSelected = frameSettings && frameSettings.frame === frame.id;
    const previewUrl = getPreviewUrl(frame, frameSettings?.orientation, frameSettings?.size);
    const title = frame.title || frame.label || frame.id;
    // choose simple label display: if variants exist show first variant label else show title
    const variantLabel = (Array.isArray(frame.variants) && frame.variants.length) ? (frame.variants[0].label || frame.variants[0].id) : null;

    return (
      <div
        key={frame.id}
        className={`frame-option ${isSelected ? 'selected' : ''}`}
        onClick={() => handleFrameChange(frame.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') handleFrameChange(frame.id); }}
      >
        {frame.id === 'none' ? (
          <div className="frame-preview no-frame">Print Only</div>
        ) : (
          <>
            <div className="frame-preview">
              <img
                src={previewUrl}
                alt={title}
                className="frame-image"
                onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER; }}
              />
            </div>
            <div className="frame-label">
              {title} {/*  && <span className="frame-variant">({variantLabel})</span> */}
            </div>
          </>
        )}
      </div>
    );
  });

  return (
    <div className="frame-selector">
      <h2>Size & Frame</h2>
      <p>Find the perfect fit for you. We offer a selection of sizes and frames that work on any wall.</p>

      {/* Materials Section */}
      <div className="option-section">
        <h3>Materials 
          <Icon
              path={mdiInformationVariantCircle }
              size={1}
              onClick={() => scrollToSectionId(
                frameSettings?.material === 'paper' ? 'premium-paper-quality' : 'canvas-quality'
              )}
              color='#555' 
            />
        </h3>
        <div className="toggle-group">
          {materialButtons.length ? materialButtons : <div>No materials configured</div>}
        </div>
      </div>

      {/* Size Section */}
      <div className="option-section">
        <h3>Size</h3>
        <select className="size-dropdown" value={frameSettings?.size || ''} onChange={handleSizeChange}>
          { sizeOptions ? sizeOptions : <option value="">No sizes</option> }
        </select>
      </div>

      {/* Orientation Section */}
      <div className="option-section">
        <h3>Orientation</h3>
        <div className="toggle-group">
          {orientationButtons}
        </div>
      </div>

      {/* Frame Selection Section (only shown for paper) */}
      { (frameSettings?.material === 'paper') && (
        <div className="option-section">
          <h3>Select a Frame</h3>
          <div className="frame-options">
            { frameItems.length ? frameItems : <div>No frames configured</div> }
          </div>
        </div>
      )}
      {/* <button style={{ marginTop: '2rem' }} onClick={scrollToSectionId(
        frameSettings?.material === 'paper' ? 'premium-paper-quality' : 'canvas-quality'
      )}>
        {frameSettings?.material === 'paper' ? 'Premium Paper Quality' : 'Canvas Quality'}
      </button> */}
    </div>
  );
};

export default FrameSelector;
